import {
    Container,
    Heading,
    Text,
    VStack,
    Box,
    Grid,
    GridItem,
    HStack,
    Badge,
    Image,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    List,
    ListItem,
    ListIcon,
    useColorModeValue,
    Icon,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { FaLeaf, FaUsers, FaHeart, FaHandsHelping } from "react-icons/fa";
import { MdCheckCircle } from "react-icons/md";

// Import your assets
import guysSmiling from "../assets/guys-smiling.jpg";
import colinSmiling from "../assets/colin-smiling.jpg";
import saunaInterior from "../assets/sauna-interior.jpg";
import coldPlunge from "../assets/cold-plunge.jpg";
import colinAsuka from "../assets/colin-asuka.jpg";
import asukaSide from "../assets/asuka-side.jpg";
import asukaInWater from "../assets/asuka-in-water.jpg";
import colinSmile from "../assets/colin-smile.jpg";
import kirillSmile from "../assets/kirill-smile.jpg";

import EmailSignup from "../components/general/EmailSignup";

// A smaller, focused component for the tab content
function PracticeTab({ imageSrc, title, description, benefits, time }) {
    return (
        <Grid
            templateColumns={{ base: "1fr", md: "1fr 1fr" }}
            gap={{ base: 6, md: 10 }}
            alignItems="center"
        >
            <GridItem>
                <Image
                    src={imageSrc}
                    alt={title}
                    borderRadius="lg"
                    boxShadow="lg"
                    objectFit="cover"
                    width="100%"
                    height={{ base: "250px", md: "400px" }}
                />
            </GridItem>
            <GridItem>
                <VStack align="start" spacing={4}>
                    <Heading size="lg">{title}</Heading>
                    <Text fontSize="md" color="gray.600">
                        {description}
                    </Text>
                    <List spacing={2}>
                        {benefits.map((benefit, index) => (
                            <ListItem key={index}>
                                <ListIcon
                                    as={MdCheckCircle}
                                    color="green.500"
                                />
                                {benefit}
                            </ListItem>
                        ))}
                    </List>
                    <Text fontSize="sm" color="gray.500" pt={2}>
                        <strong>Recommended Time:</strong> {time}
                    </Text>
                </VStack>
            </GridItem>
        </Grid>
    );
}

function Home() {
    const heroTextColor = useColorModeValue("gray.100", "gray.100");

    const images = [
        { src: colinAsuka, position: "center" },
        { src: kirillSmile, position: "center" },
        { src: asukaSide, position: "center" },
        { src: colinSmile, position: "center" },
    ];
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentImageIndex(
                (prevIndex) => (prevIndex + 1) % images.length
            );
        }, 5000); // changes image every 5 seconds
        return () => clearInterval(timer);
    }, [images.length]);

    return (
        <Box>
            {/* ====== 1. HERO SECTION ====== */}
            <Box
                position="relative"
                height="100vh"
                width="100vw"
                overflow="hidden"
            >
                {/* Background Image Slideshow */}
                {images.map((image, index) => (
                    <Box
                        key={image.src}
                        position="absolute"
                        width="100%"
                        height="100%"
                        backgroundImage={`url(${image.src})`}
                        backgroundSize="cover"
                        backgroundPosition={image.position}
                        opacity={index === currentImageIndex ? 1 : 0}
                        transition="opacity 2s ease-in-out"
                        transform="scale(1.05)"
                    />
                ))}
                {/* Dark Overlay */}
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="rgba(0,0,0,0.5)"
                />

                {/* Hero Content */}
                <Container
                    maxW="container.lg"
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    position="relative"
                >
                    <VStack
                        spacing={6}
                        textAlign="center"
                        color={heroTextColor}
                    >
                        <Heading
                            as="h1"
                            fontSize={{ base: "4xl", md: "6xl" }}
                            fontWeight="bold"
                            textShadow="0 2px 4px rgba(0,0,0,0.5)"
                        >
                            Welcome to{" "}
                            <Text as="span" color="maroon.300">
                                Rekindle
                            </Text>
                        </Heading>
                        <Text
                            fontSize={{ base: "lg", md: "xl" }}
                            maxW="600px"
                            textShadow="0 1px 3px rgba(0,0,0,0.4)"
                        >
                            Where warmth and nature create genuine connection.
                            <br />
                            Coming in early 2026.
                        </Text>
                        <Box
                            py={8}
                            px={{ base: 4, md: 8 }}
                            mt={4}
                            borderRadius="lg"
                            bg="rgba(0, 0, 30, .5)"
                            border="1px solid rgba(255, 255, 255, 0.1)"
                            backdropFilter="blur(10px)"
                            boxShadow="0 8px 32px rgba(0, 0, 0, 0.1)"
                        >
                            <EmailSignup />
                        </Box>
                    </VStack>
                </Container>
            </Box>

            {/* ====== 2. VALUES SECTION ====== */}
            <Box bg={useColorModeValue("gray.50", "gray.800")} py={16}>
                <Container maxW="container.lg">
                    <VStack spacing={4} mb={10}>
                        <Heading as="h2" size="xl">
                            Our Space, Your Journey
                        </Heading>
                        <Text
                            color="gray.500"
                            maxW="container.md"
                            textAlign="center"
                        >
                            We provide the elements for you to unwind, connect,
                            and recharge in a space that respects both nature
                            and community.
                        </Text>
                    </VStack>
                    <HStack
                        justify="center"
                        spacing={{ base: 2, md: 4 }}
                        wrap="wrap"
                    >
                        <Badge
                            variant="subtle"
                            colorScheme="green"
                            p={3}
                            borderRadius="md"
                        >
                            <Icon as={FaLeaf} mr={2} />
                            Nature-Focused
                        </Badge>
                        <Badge
                            variant="subtle"
                            colorScheme="purple"
                            p={3}
                            borderRadius="md"
                        >
                            <Icon as={FaUsers} mr={2} />
                            Community-Driven
                        </Badge>
                        <Badge
                            variant="subtle"
                            colorScheme="pink"
                            p={3}
                            borderRadius="md"
                        >
                            <Icon as={FaHeart} mr={2} />
                            Genuine Connection
                        </Badge>
                        <Badge
                            variant="subtle"
                            colorScheme="blue"
                            p={3}
                            borderRadius="md"
                        >
                            <Icon as={FaHandsHelping} mr={2} />
                            Supportive Environment
                        </Badge>
                    </HStack>
                </Container>
            </Box>

            {/* ====== 3. IMAGE CLUSTER SECTION ====== */}
            <Container maxW="container.lg" py={20}>
                <Grid
                    templateColumns={{ base: "1fr", md: "repeat(5, 1fr)" }}
                    templateRows={{ base: "auto", md: "repeat(3, 1fr)" }}
                    gap={4}
                    height={{ base: "auto", md: "600px" }}
                >
                    <GridItem
                        colSpan={{ base: 1, md: 2 }}
                        rowSpan={{ base: 1, md: 3 }}
                        backgroundImage={`url(${asukaInWater})`}
                        backgroundSize="cover"
                        backgroundPosition="center"
                        borderRadius="lg"
                        minH="300px"
                    />
                    <GridItem
                        colSpan={{ base: 1, md: 3 }}
                        rowSpan={{ base: 1, md: 1 }}
                        backgroundImage={`url(${guysSmiling})`}
                        backgroundSize="cover"
                        backgroundPosition="center"
                        borderRadius="lg"
                        minH="200px"
                    />
                    <GridItem
                        position="relative"
                        colSpan={{ base: 1, md: 3 }}
                        rowSpan={{ base: 1, md: 2 }}
                        backgroundImage={`url(${colinSmiling})`}
                        backgroundSize="cover"
                        backgroundPosition="center"
                        borderRadius="lg"
                        minH="300px"
                    >
                        <Box
                            position="absolute"
                            bottom="0"
                            left="0"
                            right="0"
                            bg="linear-gradient(to top, rgba(0,0,0,0.8), transparent)"
                            p={6}
                            borderBottomRadius="lg"
                        >
                            <Heading size="lg" color="white" mb={2}>
                                Immerse Yourself
                            </Heading>
                            <Text color="gray.200">
                                Our experiences are designed to be fun, social,
                                and deeply restorative. Leave your worries at
                                the door and step into a world of warmth and
                                connection.
                            </Text>
                        </Box>
                    </GridItem>
                </Grid>
            </Container>

            {/* ====== 4. PRACTICES (TABS) SECTION ====== */}
            <Box bg={useColorModeValue("gray.50", "gray.800")} py={20}>
                <Container maxW="container.lg">
                    <VStack spacing={4} mb={10}>
                        <Heading as="h2" size="xl">
                            Shift Your State
                        </Heading>
                        <Text color="gray.500">
                            Discover the benefits of our core practices.
                        </Text>
                    </VStack>
                    <Tabs isFitted variant="soft-rounded" colorScheme="blue">
                        <TabList mb="1em">
                            <Tab>Wood-Fired Sauna</Tab>
                            <Tab>Cold Plunge</Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel p={4}>
                                <PracticeTab
                                    imageSrc={saunaInterior}
                                    title="Authentic Wood-Fired Sauna"
                                    description="Experience the soft, enveloping heat of our Finnish-style sauna. The gentle crackle of the fire and the aroma of cedar create a tranquil space to sweat out stress and unwind."
                                    benefits={[
                                        "Relieves stress and promotes deep relaxation",
                                        "Improves circulation and cardiovascular health",
                                        "Soothes sore muscles and joint pain",
                                        "Promotes better, more restful sleep",
                                    ]}
                                    time="15-20 minutes per session"
                                />
                            </TabPanel>
                            <TabPanel p={4}>
                                <PracticeTab
                                    imageSrc={coldPlunge}
                                    title="Invigorating Cold Plunge"
                                    description="Awaken your senses and build resilience with a dip in our state-of-the-art cold plunges. A powerful way to boost recovery, enhance mental clarity, and feel vibrantly alive."
                                    benefits={[
                                        "Reduces inflammation and muscle soreness",
                                        "Boosts energy levels and mental focus",
                                        "Strengthens the immune system",
                                        "Builds mental fortitude and resilience",
                                    ]}
                                    time="1-3 minutes per plunge"
                                />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </Container>
            </Box>

            {/* ====== 5. WHY CHOOSE US / FINAL CTA ====== */}
            <Box py={20} bg={useColorModeValue("white", "gray.900")}>
                <Container maxW="container.md" textAlign="center">
                    <Heading size="xl" mb={4}>
                        Create Lasting Bonds
                    </Heading>
                    <Text fontSize="lg" color="gray.500">
                        Rekindle is on a mission to create 10,000 genuine
                        connections every year. We're more than a wellness
                        space—we're a community. Join us and build meaningful
                        relationships with friends, family, and yourself.
                    </Text>
                </Container>
            </Box>
        </Box>
    );
}

export default Home;
